/**
 * Hook to ensure analytics events are only fired once per unique key
 * Prevents duplicate events from re-renders or multiple triggers
 */

import { useRef, useCallback } from 'react';
import { analytics } from '../utils/analytics';
// analytics is already non-blocking with performantAnalytics

interface EventCache {
    [key: string]: {
        timestamp: number;
        count: number;
    };
}

/**
 * Custom hook that ensures analytics events are only fired once per unique key
 * @param dedupeWindowMs - Time window in ms to consider events as duplicates (default: 1000ms)
 */
export function useAnalyticsOnce(dedupeWindowMs: number = 1000) {
    const eventCache = useRef<EventCache>({});
    
    /**
     * Capture an analytics event only if it hasn't been fired recently
     * @param eventName - The name of the event
     * @param properties - Event properties
     * @param uniqueKey - Optional unique key for deduplication (defaults to eventName)
     */
    const captureOnce = useCallback(
        (
            eventName: string,
            properties?: Record<string, any>,
            uniqueKey?: string
        ) => {
            const key = uniqueKey || eventName;
            const now = Date.now();
            const cached = eventCache.current[key];
            
            // Check if we've fired this event recently
            if (cached && now - cached.timestamp < dedupeWindowMs) {
                console.debug(
                    `[AnalyticsOnce] Skipping duplicate event: ${eventName} (key: ${key}, count: ${cached.count + 1})`
                );
                // Update count for debugging
                eventCache.current[key] = {
                    ...cached,
                    count: cached.count + 1,
                };
                return false; // Event was not fired
            }
            
            // Fire the event and cache it
            analytics.capture(eventName, properties);
            eventCache.current[key] = {
                timestamp: now,
                count: 1,
            };
            
            // Clean up old entries after 5 minutes to prevent memory leak
            setTimeout(() => {
                if (eventCache.current[key]?.timestamp === now) {
                    delete eventCache.current[key];
                }
            }, 5 * 60 * 1000);
            
            return true; // Event was fired
        },
        [dedupeWindowMs]
    );
    
    /**
     * Clear the event cache (useful for testing or resetting state)
     */
    const clearCache = useCallback(() => {
        eventCache.current = {};
    }, []);
    
    /**
     * Get debug info about cached events
     */
    const getDebugInfo = useCallback(() => {
        const now = Date.now();
        return Object.entries(eventCache.current).map(([key, data]) => ({
            key,
            count: data.count,
            ageMs: now - data.timestamp,
            isActive: now - data.timestamp < dedupeWindowMs,
        }));
    }, [dedupeWindowMs]);
    
    return {
        captureOnce,
        clearCache,
        getDebugInfo,
    };
}

/**
 * Hook specifically for step completion events
 * Ensures each step is only marked as completed once
 */
export function useStepCompletionTracking() {
    const completedSteps = useRef<Set<string>>(new Set());
    
    const trackStepCompleted = useCallback(
        (step: string, properties?: Record<string, any>) => {
            // Check if we've already tracked this step
            if (completedSteps.current.has(step)) {
                console.debug(
                    `[StepTracking] Step already completed: ${step}`
                );
                return false;
            }
            
            // Mark step as completed and fire event
            completedSteps.current.add(step);
            analytics.capture('pairing_step_completed', {
                step,
                ...properties,
            });
            
            console.debug(`[StepTracking] Step completed: ${step}`);
            return true;
        },
        []
    );
    
    const resetSteps = useCallback(() => {
        completedSteps.current.clear();
    }, []);
    
    const isStepCompleted = useCallback((step: string) => {
        return completedSteps.current.has(step);
    }, []);
    
    return {
        trackStepCompleted,
        resetSteps,
        isStepCompleted,
    };
}